import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, Input } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { finalize, map } from 'rxjs/operators';
import { FdFieldConfigs } from '../../../shared/fd-form-components/fd-form-components.module';
import { Item } from '../../../shared/fd-form-components/fd-select/fd-select.component';
import { Messages } from '../../../shared/messages/messages';
import { ProfileModel } from '../../../shared/models/profile.model';
import { RoleModel } from '../../../shared/models/role.model';
import { DialogService } from '../../../shared/service/dialog.service';
import { ErrorService } from '../../../shared/service/error.service';
import { LoadingService } from '../../../shared/service/loading.service';
import { ProfileService } from '../../services/profile.service';
import { FdSelectConfig } from '../../../shared/fd-form-components/fd-select/fd-select.component';

const sortByDescription = (a: any, b: any) =>
  String(a?.description ?? '').localeCompare(String(b?.description ?? ''));

@Component({
    selector: 'app-update-profile',
    templateUrl: './update-profile.component.html',
  styleUrls: ['./update-profile.component.scss'],
    standalone: false
})
export class UpdateProfileComponent {

  formGroup: UntypedFormGroup;
  fields: FdFieldConfigs;
  @Input() roles: Item[];

  constructor(private formBuilder: UntypedFormBuilder,
              private profileService: ProfileService,
              private dialogRef: MatDialogRef<UpdateProfileComponent>,
              private dialogService: DialogService,
              private loadingService: LoadingService,
              private errorService: ErrorService,
              @Inject(MAT_DIALOG_DATA) public data: any

  ) {
    loadingService.show();
    this.initForm();
    this.loadRoles();
  }

  orderProfileItem(roles: Item[])
  {
    roles.sort(sortByDescription);
  }

  orderProfileRoleModel(roles: RoleModel[])
  {
    roles.sort(sortByDescription);
  }

  orderProfileProfile(profile: ProfileModel)
  {
    profile.roles.sort(sortByDescription);
  }

  initForm() {
    this.createFormGroup();
    this.orderProfileProfile(this.data?.profile);
    this.fields = this.createFields();
    this.formGroup.controls.name.setValue(this.data?.profile.description);
    this.formGroup.controls.roles.setValue(this.mapToProfileModel(this.data?.profile.roles));
  }

  createFormGroup() {
    this.formGroup = this.formBuilder.group({
      name: ['', Validators.required],
      roles: ['', Validators.min(1)]
    });
  }

  onCheckboxChange(item: Item, event: MatCheckboxChange) {
    item.selected = event.checked;

    if (item.markAll) {
      this.roles.map(x => x.selected = item.selected);
    } else {
      const selectedItems = this.roles.filter(x => x.selected && !x.markAll);
      // -1 pra desconsiderar o todos
      this.roles.filter(x => x.markAll).map(x => x.selected = selectedItems.length === this.roles.length -1);
    }

    // O valor "todos" não deve ser incluído.
    this.formGroup.controls.roles.setValue(this.roles.filter(x => x.selected && !x.markAll));

    this.orderProfileItem(this.roles);
  }

  createFields(): FdFieldConfigs {
    return {
      name: {
        label: 'Nome do profile',
        controlName: 'name',
        messages: {
          required: 'Informe um nome'
        }
      },
      roles: {
        items: [],
        label: 'Nome do profile',
        controlName: 'name',
        messages: {
          required: 'Informe ao menos uma permissão'
        }
      }
    };
  }

  loadRoles() {
    this.profileService.getRoles()
    .pipe(map(p => this.mapToProfileModel(p)),
          finalize(() => this.loadingService.hide()))
    .subscribe(data => {
      this.roles = data;
      this.orderProfileItem(this.roles);
    });
  }

  update() {
    if (!this.formGroup.valid) {
      this.dialogService.openDialog(Messages.DATA_REQUIRED_ERROR);
      return;
    }

    if (!this.formGroup.controls.roles.value || this.formGroup.controls.roles.value.length === 0) {
      this.formGroup.controls.roles.setErrors({ required: true });
      this.dialogService.openDialog(Messages.ROLE_REQUIRED_ERROR, () => this.markFormGroupTouchedAndDirty(this.formGroup));
      return;
    }

    this.loadingService.show();

    const data: ProfileModel = {
      id: this.data?.profile.id,
      description: this.formGroup.controls.name.value,
      institution: this.data?.profile.institution,
      serviceContract: this.data?.profile.serviceContract,
      roles: this.getSelectedRoles()
    }

    this.profileService.updateProfile(data).
    pipe(finalize(() => this.loadingService.hide()))
      .subscribe(() => {
        this.dialogService.openDialog(Messages.EDIT_SAVE_SUCCESS, () => this.close());
      }, (err: HttpErrorResponse) => this.errorService.handleXHRError(err, Messages.EDIT_SAVE_ERROR));
  }

  getSelectedRoles(): RoleModel[] {
    return this.formGroup.controls.roles.value.sort(sortByDescription).map(r => {
      const role: RoleModel = {id: r.value, description: r.label};
      return  role;
    });
  }



  close() {
    this.dialogRef.close();
  }



  private mapToProfileModel(response: RoleModel[]): Item[] {
    if (!response) {
      return [];
    }

    //TODO: Retirar validação quando rever tarefa de canais de credenciamento.
    let items: Item[] = [];
    response.sort(sortByDescription).forEach(role => {
      if(role['name'] !== "ACCREDITATION_CHANNELS") {
        items.push({value: role.id, label: role.description,  selected: this.data?.profile.roles.filter( p => p.id === role.id).length === 1 ? true : false})
      }
    })

    return items;

    // return response
    //   .map(value => ({
    //     value: value.id,
    //     label: value.description,
    //   }));
  }

  getInstitutionLabel() {
    if (!this.data?.profile.institution?.id) {
      return 'Todos';
    }
    return this.data?.profile.institution.id + ' - ' + this.data?.profile.institution.description;
  }

  getServiceContractLabel() {
    if (!this.data?.profile.serviceContract?.id) {
      return 'Todos';
    }
    return this.data?.profile.serviceContract.id + ' - ' + this.data?.profile.serviceContract.description;
  }

  private markFormGroupTouchedAndDirty(formGroup: UntypedFormGroup) {
    Object.values(formGroup.controls).forEach(control => {
      if (control instanceof UntypedFormGroup) {
        this.markFormGroupTouchedAndDirty(control);
      } else {
        control.markAsTouched();
        control.markAsDirty();
      }
    });
  }

}
